// react
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// tracking
import { TrackingEvent, useTracking } from 'hooks';

// components
import BookingDetails, {
  type BookingDetailsState,
} from '@karehero/llama/components/pages/BookingDetails/BookingDetails';

// hooks
import { useCareCircle } from 'hooks';

// store
import { dispatch } from 'store';
import { useInviteCareCircleMutation } from 'store/api/careCircle';
import { useGetAllRelationshipQuery } from 'store/api/relationship';

// models
import { CareCircleMemberInvite } from '@karehero/models';
import { newToast } from 'store/slices/uiSlice';

// utils
import { reportInviteError } from 'utils/inviteError';

/**
 * BookCallDetails shows the BookingDetails component.
 */
export const BookCallDetails = () => {
  // hooks
  const navigate = useNavigate();
  const { track } = useTracking();
  const { careCircle } = useCareCircle();
  const [inviteCareCircle] = useInviteCareCircleMutation();
  const { data: relationships } = useGetAllRelationshipQuery();

  // memos
  const relationshipOptions = useMemo(
    () =>
      relationships?.map((relationship) => ({
        value: relationship.id,
        label: relationship.name,
      })),
    [relationships],
  );

  // effects
  useEffect(() => {
    track(TrackingEvent.BookCallViewed, {});
  }, [track]);

  // methods
  const handleOnSubmit = useCallback(
    (data: BookingDetailsState) => {
      navigate(ROUTES.BOOK_CALL, { state: data });
    },
    [navigate],
  );

  const handleInvite = useCallback(
    async (careCircleMemberInvite: CareCircleMemberInvite) => {
      try {
        const res = await inviteCareCircle(careCircleMemberInvite).unwrap();
        dispatch(newToast({ title: 'Email invite sent', variant: 'success' }));
        return res;
      } catch (e) {
        reportInviteError(e);
      }
    },
    [inviteCareCircle],
  );

  if (!careCircle) return null;
  return (
    <BookingDetails
      careCircle={careCircle}
      onSubmit={handleOnSubmit}
      onInvite={handleInvite}
      relationshipOptions={relationshipOptions || []}
    />
  );
};

export default BookCallDetails;
